import {getAwpApiBase} from '../../../utils/awpApiBase';
import {getAwpClientHeaders} from '../../../session/awpRequestHeaders';
import {fetchWikidataEntity} from '../../../utils/wikidata';
import {isLocalEntityRef} from '../../../utils/entityLinks';
import {isBareQidLabel, resolveMutationEntityLabel} from '../ai/mutationEntityLabel';
import {
    buildEntityCardShapeProps,
    normalizeEntityCardImageUrl,
    sanitizeEntityCardPropPatch,
} from '../tldraw/customShapes/entityCardUtils';
import {ENTITY_CARD_SHAPE_TYPE} from '../tldraw/customShapes/entityCardConstants';
import {beginKbHydration, endKbHydration} from '../ai/chartAuthorship';

/**
 * @param {string} wikidataId
 * @returns {Promise<object|null>}
 */
export async function fetchKbEntityCardDetails(wikidataId) {
    if (!wikidataId) return null;
    try {
        const res = await fetch(
            `${getAwpApiBase()}/api/entities/${encodeURIComponent(wikidataId)}/summary`,
            {headers: getAwpClientHeaders()},
        );
        if (!res.ok) return null;
        const summary = await res.json();
        const canonicalName = summary?.canonicalName || summary?.label || null;
        if (!canonicalName || isBareQidLabel(canonicalName, wikidataId)) {
            return null;
        }
        return {
            id: wikidataId,
            wikidataId,
            canonicalName,
            label: canonicalName,
            description: summary?.description || '',
            imageUrl: normalizeEntityCardImageUrl(summary?.image || summary?.imageUrl || ''),
        };
    } catch {
        return null;
    }
}

/**
 * Resolve KB summary first, then Wikidata SPARQL, for card creation / hydration.
 * Local entity refs (D…) are served by the KB summary endpoint — skip the Wikidata
 * SPARQL fallback for them so hydration is not silently skipped.
 *
 * @param {string} wikidataId
 * @returns {Promise<object|null>}
 */
export async function fetchEntityDetailsForCard(wikidataId) {
    if (!wikidataId) return null;
    const kbDetails = await fetchKbEntityCardDetails(wikidataId);
    if (kbDetails) return kbDetails;
    // Local (D…) entities have no Wikidata presence — skip SPARQL.
    if (isLocalEntityRef(wikidataId)) return null;
    const wikidataDetails = await fetchWikidataEntity(wikidataId);
    if (!wikidataDetails) return null;
    const label = resolveMutationEntityLabel({
        id: wikidataId,
        wikidataId,
        canonicalName: wikidataDetails.canonicalName,
        label: wikidataDetails.label,
        labels: wikidataDetails.labels,
    });
    return {
        ...wikidataDetails,
        canonicalName: wikidataDetails.canonicalName || label,
        label,
        imageUrl: normalizeEntityCardImageUrl(
            wikidataDetails.imageUrl || wikidataDetails.image || '',
        ),
    };
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {string[]} wikidataIds
 */
export async function hydrateEntityCardsFromKb(editor, wikidataIds) {
    if (!editor || !Array.isArray(wikidataIds) || wikidataIds.length === 0) return;

    const uniqueIds = [...new Set(wikidataIds.filter(Boolean))];
    const updates = [];

    beginKbHydration();
    try {
    await Promise.all(uniqueIds.map(async (wikidataId) => {
        const details = await fetchEntityDetailsForCard(wikidataId);
        if (!details) return;

        const props = buildEntityCardShapeProps({
            id: wikidataId,
            wikidataId,
            ...details,
        });

        for (const shape of editor.getCurrentPageShapes?.() || []) {
            if (shape?.type !== ENTITY_CARD_SHAPE_TYPE) continue;
            if (shape.props?.wikidataId !== wikidataId) continue;

            const nextProps = sanitizeEntityCardPropPatch({
                label: props.label,
                description: props.description,
                imageUrl: props.imageUrl,
                h: props.h,
            });

            const changed = Object.entries(nextProps).some(([key, value]) => shape.props?.[key] !== value);
            if (!changed) continue;

            updates.push({
                id: shape.id,
                type: ENTITY_CARD_SHAPE_TYPE,
                props: nextProps,
            });
        }
    }));

    if (updates.length) {
        editor.updateShapes?.(updates);
    }
    } finally {
        endKbHydration();
    }
}
